import React, { useEffect, useState } from 'react';

import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';
import { Table } from '@buildoutinc/blueprint-react/ui/Table';

import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import LoadingSpinner from 'components/ui/loading_spinner';
import { getRaw } from 'helpers/api';

import { orientationMismatch } from '../page/document_picker';
import type { Orientation } from '../types';
import type { SlotDocument, SlotDocumentsPayload } from './types';

interface Props {
  companyId: string | null;
  pageOrientation: Orientation;
  slotDocumentsUrl: string;
  slotSettingsUrl: string;
}

const positionLabel = (document: SlotDocument) => {
  if (document.last_page) return 'Last page';
  if (document.position) return `Page ${document.position}`;

  return '---';
};

export default function SlotUsageCard({ companyId, pageOrientation, slotDocumentsUrl, slotSettingsUrl }: Props) {
  const [documents, setDocuments] = useState<SlotDocument[] | null>(null);
  const [hasLoadError, setHasLoadError] = useState(false);

  // Read only here, so the documents load once; they change on the Slot settings tab.
  useEffect(() => {
    const load = async () => {
      try {
        const response: SlotDocumentsPayload = await getRaw(
          `${slotDocumentsUrl}${companyId ? `?company_id=${companyId}` : ''}`,
        );
        setDocuments(response.documents);
        setHasLoadError(false);
      } catch {
        setHasLoadError(true);
      }
    };

    load();
  }, [slotDocumentsUrl, companyId]);

  const renderDocuments = () => {
    if (hasLoadError) {
      return (
        <Alert severity="destructive">
          <Alert.Title>The documents with the slot could not be loaded.</Alert.Title>
        </Alert>
      );
    }

    if (!documents) return <LoadingSpinner size="md" skipTopBuffer />;

    if (documents.length === 0) {
      return (
        <Empty>
          <Empty.Title>No documents have the slot yet</Empty.Title>
          <Empty.Content>Add the slot to a document in Slot settings to start using market pages.</Empty.Content>
        </Empty>
      );
    }

    return (
      <Table className="align-middle">
        <Table.Header>
          <Table.Row>
            <Table.Head>Document</Table.Head>
            <Table.Head>Slot position</Table.Head>
            <Table.Head>This page</Table.Head>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {documents.map(document => (
            <Table.Row key={document.document_template_id}>
              <Table.Cell>{document.document_template_name}</Table.Cell>
              <Table.Cell className="text-muted">{positionLabel(document)}</Table.Cell>
              <Table.Cell className="text-muted">
                {orientationMismatch(document, pageOrientation)
                  ? `Never used: the document is ${document.orientation}`
                  : 'Fits'}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    );
  };

  return (
    <Card className="mb-4">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-1">
          <h5 className="mb-0">How this page gets into documents</h5>
          <Button href={slotSettingsUrl} size="sm" variant="secondary">Slot settings</Button>
        </div>
        <p className="text-muted mb-3">
          This page isn&apos;t placed in documents directly. The Market Overview slot pulls it in when a
          property matches its market.
        </p>
        {renderDocuments()}
      </Card.Body>
    </Card>
  );
}
